import { Checkbox } from 'primereact/checkbox';
import * as React from 'react';
import {
  Button,
  FormGroup,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import {
  CheckBoxLabelContainer,
  CheckBoxLabel,
} from '../../components/Checkbox/pickpackCheckbox';
import { LayoutStoreInstance } from '../../LayoutStore';
import { centsToPrice } from '@/utils/formatter';
import { DEFAULT_CURRENCY, PriceMultiplierByCurrency } from '@/utils/price';
import { useAddNewContainerTypeMutation } from '../../graphql/mutations/addContainerType.generated';
import { useGetAllContainerTypeGroupsQuery } from '../../graphql/queries/getAllContainerTypeGroups.generated';
import i18n from '@/utils/i18n';
import { ContainerTypeSegment } from '../../graphql/generatedTypes';
import {
  defaultNewContainerType,
  type NewContainerType,
} from './newContainerType';

type Props = {
  isAddContainerTypeModalOpen: boolean;
  setIsAddContainerTypeModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  newContainerType: NewContainerType;
  setNewContainerType: React.Dispatch<React.SetStateAction<NewContainerType>>;
  currency: string;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  load: Function;
};

export function AddContainerTypeModal({
  isAddContainerTypeModalOpen,
  setIsAddContainerTypeModalOpen,
  newContainerType,
  setNewContainerType,
  currency,
  setIsLoading,
  load,
}: Props) {
  const [addNewContainerTypeMutation] = useAddNewContainerTypeMutation();

  const { data: containerTypeGroupsData } = useGetAllContainerTypeGroupsQuery({
    fetchPolicy: 'no-cache',
  });

  /**
   * Gets new container prices for currency.
   * If there are no prices for that currency add default values
   * @returns Price of new container type for currency
   */
  function getNewContainerTypePrices() {
    let prices: any = newContainerType.pricesByCurrency[currency];
    if (prices == null) {
      prices = { ...newContainerType.pricesByCurrency[DEFAULT_CURRENCY] };
      const multiplier = (PriceMultiplierByCurrency as any)[currency];
      for (const attr of Object.keys(prices)) {
        // allow accessing the attributes via keys
        // @ts-ignore
        prices[attr] = String(
          Math.floor(parseFloat(prices[attr]) * multiplier * 1000) / 1000,
        );
      }
      setNewContainerType({
        ...newContainerType,
        pricesByCurrency: {
          ...newContainerType.pricesByCurrency,
          [currency]: prices,
        },
      });
    }
    return prices;
  }

  function setNewContainerTypePrice(newVal: string, name: string) {
    const prices: any = getNewContainerTypePrices();
    prices[name] = newVal.length > 0 ? newVal : '0';
    setNewContainerType({
      ...newContainerType,
      pricesByCurrency: {
        ...newContainerType.pricesByCurrency,
        [currency]: prices,
      },
    });
  }

  /**
   * Adds container type to backend
   */
  async function addContainerType() {
    setIsLoading(true);
    setIsAddContainerTypeModalOpen(false);
    const result = await addNewContainerTypeMutation({
      variables: {
        container_type_name: newContainerType.name,
        is_white_label: newContainerType.isWhiteLabel,
        prices_by_currency_string: JSON.stringify(
          newContainerType.pricesByCurrency,
        ),
        group_id: newContainerType.groupId ?? undefined,
        segment: newContainerType.segment as ContainerTypeSegment,
      },
    });
    if (result.errors != null || !result.data?.addNewContainerType) {
      LayoutStoreInstance.setError(
        i18n.t('AddContainerTypeModal:errorAddingContainerType'),
      );
      console.log(result.errors);
    }
    setNewContainerType({ ...defaultNewContainerType });
    await load();
  }

  return (
    <Modal
      isOpen={isAddContainerTypeModalOpen}
      toggle={() => setIsAddContainerTypeModalOpen(false)}
    >
      <ModalHeader>{i18n.t('AddContainerTypeModal:title')}</ModalHeader>
      <ModalBody>
        <FormGroup>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('AddContainerTypeModal:containerTypeName')}
            </span>
            <input
              type="text"
              className="form-control"
              value={newContainerType.name}
              onChange={(e) =>
                setNewContainerType({
                  ...newContainerType,
                  name: e.target.value,
                })
              }
            />
          </div>
          <div className="input-group mb-3">
            <span className="input-group-text" id="group-select">
              {i18n.t('AddContainerTypeModal:containerTypeGroup')}
            </span>
            <select
              className="form-control"
              value={newContainerType.groupId ?? ''}
              onChange={(e) =>
                setNewContainerType({
                  ...newContainerType,
                  groupId: e.target.value ? parseInt(e.target.value) : null,
                })
              }
            >
              <option value="">
                {i18n.t('AddContainerTypeModal:noGroup')}
              </option>
              {containerTypeGroupsData?.getAllContainerTypeGroups.map(
                (group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ),
              )}
            </select>
          </div>
          <div className="input-group mb-3">
            <span className="input-group-text" id="segment-select">
              {i18n.t('AddContainerTypeModal:segment')}
            </span>
            <select
              className="form-control"
              value={newContainerType.segment ?? ''}
              onChange={(e) =>
                setNewContainerType({
                  ...newContainerType,
                  segment: e.target.value || null,
                })
              }
            >
              <option value="">
                {i18n.t('AddContainerTypeModal:selectSegment')}
              </option>
              <option value="DRINKS">
                {i18n.t('EditContainerInfo:segmentDrinks')}
              </option>
              <option value="FOOD">
                {i18n.t('EditContainerInfo:segmentFood')}
              </option>
              <option value="CUTLERY">
                {i18n.t('EditContainerInfo:segmentCutlery')}
              </option>
            </select>
          </div>
          <CheckBoxLabelContainer
            onClick={() =>
              setNewContainerType({
                ...newContainerType,
                isWhiteLabel: !newContainerType.isWhiteLabel,
              })
            }
          >
            <Checkbox checked={newContainerType.isWhiteLabel} />
            <CheckBoxLabel>
              {i18n.t('AddContainerTypeModal:isWhiteLabel')}
            </CheckBoxLabel>
          </CheckBoxLabelContainer>
          <Label style={{ margin: '0.5em' }}>
            {i18n.t('AddContainerTypeModal:prices')}:
          </Label>
          <div style={{ margin: '0.5em' }} className="input-group">
            <div className="input-group-text">
              {i18n.t('AddContainerTypeModal:transactionPrice')}
            </div>
            <input
              value={getNewContainerTypePrices().transactionPrice}
              type="text"
              className="form-control"
              onChange={(e) =>
                setNewContainerTypePrice(e.target.value, 'transactionPrice')
              }
            />
            <span className="input-group-text">
              {' '}
              {centsToPrice(
                parseFloat(getNewContainerTypePrices().transactionPrice) * 100,
                currency,
                3,
              )}
            </span>
          </div>
          <div style={{ margin: '0.5em' }} className="input-group">
            <div className="input-group-text">
              {i18n.t('AddContainerTypeModal:overdueCost')}
            </div>
            <input
              value={getNewContainerTypePrices().overduePrice}
              type="text"
              className="form-control"
              onChange={(e) =>
                setNewContainerTypePrice(e.target.value, 'overduePrice')
              }
            />
            <span className="input-group-text">
              {' '}
              {centsToPrice(
                parseInt(getNewContainerTypePrices().overduePrice),
                currency,
              )}
            </span>
          </div>
          <div style={{ margin: '0.5em' }} className="input-group">
            <div className="input-group-text">
              {i18n.t('AddContainerTypeModal:creditsOnOverdueReturn')}
            </div>
            <input
              value={getNewContainerTypePrices().creditsOnOverdueReturn}
              type="text"
              className="form-control"
              onChange={(e) =>
                setNewContainerTypePrice(
                  e.target.value,
                  'creditsOnOverdueReturn',
                )
              }
            />
            <span className="input-group-text">
              {' '}
              {centsToPrice(
                parseInt(getNewContainerTypePrices().creditsOnOverdueReturn),
                currency,
              )}
            </span>
          </div>
          <div style={{ margin: '0.5em' }} className="input-group">
            <div className="input-group-text">
              {i18n.t('AddContainerTypeModal:storeLostCost')}
            </div>
            <input
              value={getNewContainerTypePrices().storeLostCost}
              type="text"
              className="form-control"
              onChange={(e) =>
                setNewContainerTypePrice(e.target.value, 'storeLostCost')
              }
            />
            <span className="input-group-text">
              {' '}
              {centsToPrice(
                parseInt(getNewContainerTypePrices().storeLostCost),
                currency,
              )}
            </span>
          </div>
          <div style={{ margin: '0.5em' }} className="input-group">
            <div className="input-group-text">
              {i18n.t('AddContainerTypeModal:storeLostCostUpper')}
            </div>
            <input
              value={getNewContainerTypePrices().storeLostCostUpper}
              type="text"
              className="form-control"
              onChange={(e) =>
                setNewContainerTypePrice(e.target.value, 'storeLostCostUpper')
              }
            />
            <span className="input-group-text">
              {' '}
              {centsToPrice(
                parseInt(getNewContainerTypePrices().storeLostCostUpper),
                currency,
              )}
            </span>
          </div>
          <div style={{ margin: '0.5em' }} className="input-group">
            <div className="input-group-text">
              {i18n.t('AddContainerTypeModal:storeLostCostLower')}
            </div>
            <input
              value={getNewContainerTypePrices().storeLostCostLower}
              type="text"
              className="form-control"
              onChange={(e) =>
                setNewContainerTypePrice(e.target.value, 'storeLostCostLower')
              }
            />
            <span className="input-group-text">
              {' '}
              {centsToPrice(
                parseInt(getNewContainerTypePrices().storeLostCostLower),
                currency,
              )}
            </span>
          </div>
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button
          style={{ margin: '0.5em' }}
          color="primary"
          className="text-white"
          disabled={!newContainerType.segment}
          onClick={addContainerType}
        >
          {i18n.t('AddContainerTypeModal:addContainerType')}
        </Button>
        <Button
          style={{ margin: '0.5em' }}
          color="secondary"
          onClick={() => setIsAddContainerTypeModalOpen(false)}
        >
          {i18n.t('AddContainerTypeModal:close')}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
